import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { portal as portalApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Markdown from '../components/Markdown';
import { statusLabel } from '../utils/portal';

const STATUS_TEXT = {
  pending: 'Not discussed',
  discussed: 'Discussed',
  deferred: 'Deferred',
  skipped: 'Skipped — left out of the minutes',
};

const splitNames = (text) => (text || '').split(/[,\n]/).map((n) => n.trim()).filter(Boolean);

/**
 * Second stage: the raw notes taken during the meeting sit beside the version
 * that goes to the board. Sections that group several agenda items get one
 * outcome line per item (the committee's minutes style), each shown with its
 * agenda text so the context isn't lost; the section's own box holds anything
 * else. Compiling assembles all of it into the meeting's minutes.
 */
export default function ReviewMeetingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isChair } = useAuth();
  const canManage = isAdmin || isChair;

  const [meeting, setMeeting] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [itemDrafts, setItemDrafts] = useState({}); // { topicId: { itemKey: text } }
  const [savingId, setSavingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [roster, setRoster] = useState([]);
  const [present, setPresent] = useState([]); // roster names checked off
  const [guests, setGuests] = useState('');   // anyone else, comma-separated
  const [people, setPeople] = useState([]);   // roster with ids, for assignees
  const [drafting, setDrafting] = useState(false);
  const [drafted, setDrafted] = useState({}); // box id -> true while it still holds Claude's text
  const [suggested, setSuggested] = useState(null); // { actions, gaps } from the last draft
  const timers = useRef({});
  // The meeting page's "Draft Minutes" button lands here with ?draft=1.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoDraft = useRef(searchParams.get('draft') === '1');

  const load = useCallback(async () => {
    try {
      const data = await portalApi.getTopics(id);
      setMeeting(data.meeting);
      setTopics(data.topics);
      // Attendees is stored as a plain comma-separated list; split it back
      // into roster checkboxes and everyone else.
      const volunteers = await portalApi.listVolunteers().catch(() => []);
      setPeople(volunteers);
      const names = volunteers.map((p) => p.display_name);
      const listed = splitNames(data.meeting.attendees);
      const onRoster = new Set(names.map((n) => n.toLowerCase()));
      setRoster(names);
      setPresent(listed.filter((n) => onRoster.has(n.toLowerCase())));
      setGuests(listed.filter((n) => !onRoster.has(n.toLowerCase())).join(', '));
      const seeded = {};
      const seededItems = {};
      data.topics.forEach((t) => {
        seeded[t.id] = t.recorded_notes || '';
        seededItems[t.id] = {};
        (t.items || []).forEach((it) => { seededItems[t.id][it.key] = it.outcome || ''; });
      });
      setDrafts(seeded);
      setItemDrafts(seededItems);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // A tab closed mid-keystroke would drop the last edit; say so rather than
  // silently losing it.
  useEffect(() => {
    const warn = (e) => {
      if (Object.keys(timers.current).length === 0) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  // Debounced autosave. Each box has its own timer, so typing in one item
  // never delays or drops another's save.
  const debounceSave = (timerId, topicId, patch) => {
    setSavingId(topicId);
    if (timers.current[timerId]) clearTimeout(timers.current[timerId].handle);
    const commit = async () => {
      delete timers.current[timerId];
      try {
        const updated = await portalApi.updateTopic(topicId, patch);
        // Refresh the topic but not the drafts: those hold what's being typed.
        setTopics((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      } catch (err) {
        setMessage(`Error: ${err.message}`);
      } finally {
        setSavingId((cur) => (cur === topicId ? null : cur));
      }
    };
    timers.current[timerId] = { handle: setTimeout(commit, 700), commit };
  };

  // Typing in a box Claude filled makes it the person's own text again.
  const unmark = (boxId) => setDrafted((d) => {
    if (!d[boxId]) return d;
    const rest = { ...d };
    delete rest[boxId];
    return rest;
  });

  const saveRecorded = (topicId, value) => {
    setDrafts((d) => ({ ...d, [topicId]: value }));
    unmark(topicId);
    debounceSave(topicId, topicId, { recorded_notes: value });
  };

  const saveOutcome = (topicId, key, value) => {
    setItemDrafts((d) => ({ ...d, [topicId]: { ...(d[topicId] || {}), [key]: value } }));
    unmark(`${topicId}:${key}`);
    debounceSave(`${topicId}:${key}`, topicId, { item_outcome: { key, text: value } });
  };

  // The server drafts in the background (Claude can outlast CloudFront's
  // 30-second timeout); check back every few seconds until it's done.
  const waitForDraft = async () => {
    const giveUpAt = Date.now() + 5 * 60 * 1000;
    while (Date.now() < giveUpAt) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const r = await portalApi.getDraft(id);
      if (r.status === 'done') return r;
    }
    throw new Error('Claude is taking more than 5 minutes — try again later');
  };

  // Claude writes up the boxes that are still empty; anything already typed
  // is left alone, here and on the server.
  const draftWithClaude = async () => {
    setDrafting(true);
    setMessage(null);
    try {
      await flushPending();
      await portalApi.startDraft(id);
      const r = await waitForDraft();
      let filled = 0;
      const marks = {};
      r.topics.forEach((s) => {
        Object.entries(s.item_outcomes).forEach(([key, text]) => {
          if (((itemDrafts[s.topic_id] || {})[key] || '').trim()) return;
          saveOutcome(s.topic_id, key, text);
          marks[`${s.topic_id}:${key}`] = true;
          filled++;
        });
        if (s.notes && !(drafts[s.topic_id] || '').trim()) {
          saveRecorded(s.topic_id, s.notes);
          marks[s.topic_id] = true;
          filled++;
        }
      });
      setDrafted((d) => ({ ...d, ...marks }));
      setSuggested({
        actions: r.action_items.map((a, n) => ({ ...a, n, keep: true, created: false })),
        gaps: r.gaps,
      });
      setMessage(`Claude filled ${filled} empty box${filled === 1 ? '' : 'es'} and suggested `
        + `${r.action_items.length} action item${r.action_items.length === 1 ? '' : 's'}. `
        + 'Drafted text is highlighted until you edit it — read each one against the raw notes.');
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setDrafting(false);
    }
  };

  // Start the draft once the page has loaded, and only once — drop the
  // ?draft=1 so a reload doesn't run it again.
  useEffect(() => {
    if (loading || !autoDraft.current) return;
    autoDraft.current = false;
    setSearchParams({}, { replace: true });
    if (topics.some((t) => (t.live_notes || '').trim())) draftWithClaude();
    else setMessage('There are no raw notes to draft from yet — take notes in Run Meeting first.');
  }, [loading]); // eslint-disable-line

  const editSuggestion = (n, patch) => setSuggested((s) => ({
    ...s, actions: s.actions.map((a) => (a.n === n ? { ...a, ...patch } : a)),
  }));

  const createActions = async () => {
    const chosen = suggested.actions.filter((a) => a.keep && !a.created && a.title.trim());
    try {
      for (const a of chosen) {
        await portalApi.createAction({
          title: a.title.trim(),
          details: a.details || null,
          assignee_id: a.assignee_id || null,
          due_date: a.due_date || null,
          meeting_id: id,
        });
        editSuggestion(a.n, { created: true });
      }
      setMessage(`Added ${chosen.length} action item${chosen.length === 1 ? '' : 's'}.`);
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const saveAttendance = (nextPresent, nextGuests) => {
    setPresent(nextPresent);
    setGuests(nextGuests);
    const byRoster = roster.filter((n) => nextPresent.includes(n));
    const attendees = [...byRoster, ...splitNames(nextGuests)].join(', ');
    if (timers.current.attendance) clearTimeout(timers.current.attendance.handle);
    const commit = async () => {
      delete timers.current.attendance;
      try {
        await portalApi.updateMeeting(id, { attendees });
        setMeeting((m) => ({ ...m, attendees }));
      } catch (err) {
        setMessage(`Error: ${err.message}`);
      }
    };
    timers.current.attendance = { handle: setTimeout(commit, 700), commit };
  };

  const togglePresent = (name) => saveAttendance(
    present.includes(name) ? present.filter((n) => n !== name) : [...present, name],
    guests,
  );

  // Compiling reads from the server, so anything still sitting in a debounce
  // has to land first — otherwise the last thing typed misses the minutes.
  const flushPending = async () => {
    const pending = Object.values(timers.current);
    pending.forEach((t) => clearTimeout(t.handle));
    await Promise.all(pending.map((t) => t.commit()));
  };

  // Starting point for tidying: the raw notes, copied across.
  const copyLive = (topic) => saveRecorded(topic.id, topic.live_notes || '');

  const compile = async (save) => {
    if (save && (meeting.minutes_md || '').trim() && !window.confirm(
      'Replace the current minutes draft with this version? '
      + 'The current draft is kept and can be restored from the meeting page.'
    )) return;
    try {
      await flushPending();
      const r = await portalApi.compileTopics(id, save);
      if (save) {
        const extras = [
          r.left_out.length ? `${r.left_out.length} not discussed and left out` : '',
          r.skipped.length ? `${r.skipped.length} skipped` : '',
        ].filter(Boolean).join(', ');
        const saved = `Minutes assembled from ${r.topics_included} sections${extras ? ` (${extras})` : ''}.`
          + `${r.replaced_previous ? ' The previous draft was kept.' : ''}`;
        setMeeting((m) => ({ ...m, minutes_md: r.minutes_md }));
        setPreview(null);
        // Head back to the meeting's minutes to review and publish — unless
        // Claude's suggested action items haven't been added yet.
        const unadded = (suggested?.actions || []).filter((a) => a.keep && !a.created).length;
        if (unadded && !window.confirm(
          `Minutes saved. ${unadded} suggested action item${unadded === 1 ? ' hasn\'t' : 's haven\'t'} been added yet `
          + '— leave this page anyway?'
        )) {
          setMessage(`${saved} Add the ticked action items below, then open the meeting to publish.`);
          return;
        }
        navigate(`/volunteer/meetings/${id}?tab=minutes`, { state: { flash: `${saved} Review and publish below.` } });
      } else {
        setPreview(r.minutes_md);
      }
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  if (loading) return <div className="loading">Loading…</div>;
  if (!meeting) {
    return (
      <div className="portal-page">
        <div className="alert alert-error">{message || 'Meeting not found'}</div>
        <Link to="/volunteer/meetings">← All meetings</Link>
      </div>
    );
  }

  const withNotes = topics.filter((t) => (t.live_notes || '').trim()).length;
  const recorded = topics.filter((t) => (drafts[t.id] || '').trim()
    || Object.values(itemDrafts[t.id] || {}).some((v) => (v || '').trim())).length;

  return (
    <div className="portal-page review-page">
      <div className="portal-header">
        <div>
          <p className="portal-subtitle">
            <Link to={`/volunteer/meetings/${id}`}>← Back to the meeting</Link>
            {' · '}
            <Link to={`/volunteer/meetings/${id}/run`}>Return to the runner</Link>
          </p>
          <h1>Review &amp; record</h1>
          <p className="portal-subtitle">
            {meeting.title} — {withNotes} of {topics.length} topics have raw notes, {recorded} written up.
          </p>
        </div>
        {canManage && (
          <div className="portal-header-links">
            <button className="btn-sm btn-secondary" onClick={draftWithClaude} disabled={drafting || withNotes === 0}
              title="Write up the empty boxes from the raw notes and suggest action items">
              {drafting ? 'Drafting… (up to a minute)' : 'Draft with Claude'}
            </button>
            <button className="btn-sm btn-secondary" onClick={() => compile(false)}>Preview minutes</button>
            <button className="btn-sm btn-primary" onClick={() => compile(true)}>Save as the minutes</button>
          </div>
        )}
      </div>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      {preview !== null && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div className="modal-content review-preview" onClick={(e) => e.stopPropagation()}>
            <h3>Minutes preview</h3>
            <div className="review-preview-body"><Markdown>{preview}</Markdown></div>
            <div className="portal-form-actions">
              <button className="btn-sm btn-secondary" onClick={() => setPreview(null)}>Close</button>
              <button className="btn-sm btn-primary" onClick={() => compile(true)}>Save as the minutes</button>
            </div>
          </div>
        </div>
      )}

      <section className="review-topic review-attendance">
        <div className="review-topic-head">
          <h2>Attendance</h2>
          <span className="portal-muted">{present.length + splitNames(guests).length} present</span>
        </div>
        <div className="review-attendance-list">
          {roster.map((name) => (
            <label key={name} className="review-attendee">
              <input type="checkbox" checked={present.includes(name)} disabled={!canManage}
                onChange={() => togglePresent(name)} />
              {name}
            </label>
          ))}
        </div>
        <label className="review-guests">Others present
          <input type="text" value={guests} disabled={!canManage}
            placeholder="Guests or members without a login, separated by commas"
            onChange={(e) => saveAttendance(present, e.target.value)} />
        </label>
        <p className="portal-muted">Anyone on the roster left unchecked is listed as absent in the minutes.</p>
      </section>

      {suggested && (
        <section className="review-topic review-suggested">
          <div className="review-topic-head">
            <h2>From Claude's draft</h2>
            <button className="btn-sm btn-secondary" onClick={() => setSuggested(null)}>Dismiss</button>
          </div>

          {suggested.gaps.length > 0 && (
            <>
              <h4>Check before sending</h4>
              <ul className="review-gaps">
                {suggested.gaps.map((g) => <li key={g}>{g}</li>)}
              </ul>
            </>
          )}

          <h4>Suggested action items</h4>
          {suggested.actions.length === 0 ? (
            <p className="portal-muted">None found in the notes.</p>
          ) : (
            <>
              <div className="review-actions">
                {suggested.actions.map((a) => (
                  <div key={a.n} className={`review-action${a.created ? ' review-action-done' : ''}`}>
                    <input type="checkbox" checked={a.keep} disabled={a.created}
                      onChange={(e) => editSuggestion(a.n, { keep: e.target.checked })} />
                    <div className="review-action-fields">
                      <input type="text" value={a.title} disabled={a.created}
                        onChange={(e) => editSuggestion(a.n, { title: e.target.value })} />
                      <div className="review-action-meta">
                        <select value={a.assignee_id} disabled={a.created}
                          onChange={(e) => editSuggestion(a.n, { assignee_id: e.target.value })}>
                          <option value="">Unassigned</option>
                          {people.map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                        </select>
                        <input type="date" value={a.due_date} disabled={a.created}
                          onChange={(e) => editSuggestion(a.n, { due_date: e.target.value })} />
                        {a.topic_id && (
                          <span className="portal-muted">
                            {(topics.find((t) => t.id === a.topic_id) || {}).title}
                          </span>
                        )}
                        {a.created && <span className="portal-badge badge-discussed">Added</span>}
                      </div>
                      {a.details && <div className="portal-muted review-action-details">{a.details}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="portal-form-actions">
                <button className="btn-sm btn-primary" onClick={createActions}
                  disabled={!suggested.actions.some((a) => a.keep && !a.created)}>
                  Add ticked items to Action Items
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {topics.map((topic, i) => (
        <section key={topic.id} className={`review-topic status-${topic.status}`}>
          <div className="review-topic-head">
            <h2>{i + 1}. {topic.title}</h2>
            <span className={`portal-badge badge-${topic.status}`}>{STATUS_TEXT[topic.status] || statusLabel(topic.status)}</span>
          </div>

          <div className="review-cols">
            <div className="review-col">
              <h4>Raw notes from the meeting</h4>
              {(topic.live_notes || '').trim() ? (
                <pre className="review-live">{topic.live_notes}</pre>
              ) : (
                <p className="portal-muted">Nothing captured.</p>
              )}
              {(topic.live_notes || '').trim() && (
                <button className="btn-sm btn-secondary" onClick={() => copyLive(topic)}>
                  Copy across →
                </button>
              )}
            </div>

            <div className="review-col">
              <h4>
                For the board
                {savingId === topic.id && <span className="run-save run-save-saving"> Saving…</span>}
              </h4>

              {(topic.items || []).length > 0 ? (
                <div className="review-items">
                  {topic.items.map((it, n) => (
                    <div key={it.key} className="review-item">
                      {it.group && it.group !== (topic.items[n - 1] || {}).group && (
                        <div className="review-item-group">{it.group}</div>
                      )}
                      <div className="review-item-title">{it.title}</div>
                      {it.context && (
                        <details className="review-item-context">
                          <summary>{it.context.split('\n')[0]}</summary>
                          <Markdown>{it.context}</Markdown>
                        </details>
                      )}
                      <textarea
                        className={`review-item-outcome${drafted[`${topic.id}:${it.key}`] ? ' review-drafted' : ''}`}
                        rows={2}
                        value={(itemDrafts[topic.id] || {})[it.key] || ''}
                        onChange={(e) => saveOutcome(topic.id, it.key, e.target.value)}
                        placeholder="Outcome for the minutes — leave blank if not discussed"
                        disabled={topic.status === 'skipped'}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                topic.agenda_md && (
                  <details className="review-agenda">
                    <summary>Agenda text for this section</summary>
                    <Markdown>{topic.agenda_md}</Markdown>
                  </details>
                )
              )}

              {(topic.items || []).length > 0 && <h5 className="review-general-label">Other notes for this section</h5>}
              <textarea
                className={`review-recorded${drafted[topic.id] ? ' review-drafted' : ''}`}
                value={drafts[topic.id] || ''}
                onChange={(e) => saveRecorded(topic.id, e.target.value)}
                placeholder={topic.status === 'skipped'
                  ? 'Skipped — this topic will be left out of the minutes.'
                  : (topic.items || []).length > 0
                    ? 'Anything not tied to one item above.'
                    : 'The version that goes in the minutes.'}
                disabled={topic.status === 'skipped'}
              />
            </div>
          </div>
        </section>
      ))}

      <div className="portal-form-actions">
        <button className="btn-sm btn-secondary" onClick={() => navigate(`/volunteer/meetings/${id}/run`)}>
          ← Return to the runner
        </button>
        {canManage && (
          <button className="btn-sm btn-primary" onClick={() => compile(true)}>Save as the minutes</button>
        )}
      </div>
    </div>
  );
}
